// ============================================================================
// useAvisoAniversario — bolinha vermelha de aniversário no menu (16/10/2026)
// ============================================================================
// Pedido do dono: "um AVISO DE ANIVERSÁRIO tipo o do Financeiro que fez na
// Auditoria, com uma BOLINHA VERMELHA indicando que tem uma atualização".
// Só front, sem servidor e sem migration (no padrão do useFinAlertas):
//   - conta os aniversariantes de HOJE (dia de São Paulo) da rede inteira, os
//     mesmos que a página /gestao/aniversarios mostra em "Hoje"; o cadastro
//     que o app já tem traz dia e mês para todos os cargos;
//   - recalcula ao voltar o foco, ao mudar a visibilidade, quando outra aba
//     marca como visto e a cada minuto (virada do dia);
//   - abrir a página apaga: marcarAvisoAniversarioVisto grava
//     `fk_aniv_visto:<user.id>` = dia SP no localStorage (try/catch; se o
//     armazenamento falhar, fica na memória até recarregar). Volta no próximo
//     dia com aniversário. Limitação: a marca é por aparelho.
// Uso: const aviso = useAvisoAniversario({ professionals, userId: user?.id });
//      aviso.badge (número para a pílula; 0 = nada), aviso.pendente.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { hojeSP } from './dataSP.js';
import { aniversariantesHoje, deveMostrarSelo } from './aniversariosHoje.js';

export const ROTA_ANIVERSARIOS = '/gestao/aniversarios';
export const INTERVALO_MS = 60 * 1000;
export const chaveVisto = (userId) => `fk_aniv_visto:${userId || 'anon'}`;

// ─── Marca "visto hoje" (por aparelho) ─────────────────────────────────────
const memoria = new Map(); // reserva quando o localStorage falha
const ouvintes = new Set();
function emitir() {
  for (const fn of [...ouvintes]) { try { fn(); } catch { /* um ouvinte não derruba os outros */ } }
}
function armazenamento() {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
}

export function lerAvisoVisto(userId) {
  const k = chaveVisto(userId);
  try {
    const v = armazenamento()?.getItem(k);
    if (v) return v;
  } catch { /* storage bloqueado: usa a memória */ }
  return memoria.get(k) || null;
}

export function marcarAvisoAniversarioVisto(userId, dia = hojeSP()) {
  const k = chaveVisto(userId);
  const antes = lerAvisoVisto(userId);
  memoria.set(k, dia);
  try { armazenamento()?.setItem(k, dia); } catch { /* fica só na memória */ }
  if (antes !== dia) emitir();
}

// ─── Um relógio só para todas as telas que usam o aviso ────────────────────
let ativos = 0;
let timer = null;
const aoMudar = () => emitir();
function ligar() {
  ativos += 1;
  if (ativos > 1 || typeof window === 'undefined') return;
  timer = setInterval(aoMudar, INTERVALO_MS);
  window.addEventListener('focus', aoMudar);
  window.addEventListener('storage', aoMudar);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', aoMudar);
}
function desligar() {
  ativos = Math.max(0, ativos - 1);
  if (ativos > 0 || typeof window === 'undefined') return;
  clearInterval(timer);
  timer = null;
  window.removeEventListener('focus', aoMudar);
  window.removeEventListener('storage', aoMudar);
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', aoMudar);
}

export function useAvisoAniversario({ professionals, userId } = {}) {
  const [hoje, setHoje] = useState(() => hojeSP());
  const [, setVersao] = useState(0);
  const vistoEm = lerAvisoVisto(userId);
  const atual = useRef({ userId, vistoEm });
  atual.current = { userId, vistoEm };

  useEffect(() => {
    const fn = () => {
      setHoje(hojeSP());
      const { userId: uid, vistoEm: antes } = atual.current;
      if (lerAvisoVisto(uid) !== antes) setVersao(v => v + 1);
    };
    ouvintes.add(fn);
    ligar();
    return () => { ouvintes.delete(fn); desligar(); };
  }, []);

  const n = useMemo(() => aniversariantesHoje(professionals || [], hoje).length, [professionals, hoje]);
  const pendente = deveMostrarSelo({ n, vistoEm, hoje });
  return { n, hoje, vistoEm, pendente, badge: pendente ? n : 0 };
}

export default useAvisoAniversario;
